import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { CUSTOMER_STATUS_LABELS, type CustomerStatus } from "@/lib/customer-status";
import type { CustomerListOwnerFilter } from "@/lib/customer-list-calc";

export interface CustomerListToolbarState {
  search: string;
  owner: CustomerListOwnerFilter;
  status: CustomerStatus | "all";
}

interface CustomerListToolbarProps {
  state: CustomerListToolbarState;
  onChange: (patch: Partial<CustomerListToolbarState>) => void;
  unownedCount: number;
  /** Lista er gruppert etter hast (ingen kolonnesortering valgt). */
  groupedByUrgency: boolean;
  onGroupByUrgency: () => void;
}

const ALL = "all";

/** Søk, eier, status og «Haster mest» (nullstiller kolonnesorteringen). */
export default function CustomerListToolbar({ state, onChange, unownedCount, groupedByUrgency, onGroupByUrgency }: CustomerListToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-[16rem] flex-1">
        <SrIcon name="search" className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={state.search}
          onChange={(e) => onChange({ search: e.target.value })}
          placeholder="Søk på navn, org.nr, kontaktperson, e-post eller bransje"
          className="pl-8"
          aria-label="Søk i kunder"
        />
      </div>
      <Select value={state.owner} onValueChange={(v) => onChange({ owner: v as CustomerListOwnerFilter })}>
        <SelectTrigger className="w-40" aria-label="Filtrer på eier">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Alle eiere</SelectItem>
          <SelectItem value="mine">Mine kunder</SelectItem>
          <SelectItem value="none">Uten eier · {unownedCount}</SelectItem>
        </SelectContent>
      </Select>
      <Select value={state.status} onValueChange={(v) => onChange({ status: v as CustomerStatus | "all" })}>
        <SelectTrigger className="w-40" aria-label="Filtrer på status">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Alle statuser</SelectItem>
          {(Object.keys(CUSTOMER_STATUS_LABELS) as CustomerStatus[]).map((st) => (
            <SelectItem key={st} value={st}>{CUSTOMER_STATUS_LABELS[st]}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        variant={groupedByUrgency ? "default" : "outline"}
        size="sm"
        aria-pressed={groupedByUrgency}
        onClick={onGroupByUrgency}
        title="Grupper etter hva som haster. Klikk en kolonneoverskrift for å sortere flatt."
      >
        Haster mest
      </Button>
    </div>
  );
}
